import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { NotificationRow } from "@/components/community/types";

export function useNotifications(userId?: string) {
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const { data } = await supabase
      .from("community_notifications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(30);
    setItems(((data as any) || []) as NotificationRow[]);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetchItems();
    const ch = supabase
      .channel(`notif-${userId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "community_notifications", filter: `user_id=eq.${userId}` },
        () => fetchItems())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [userId, fetchItems]);

  const unread = items.filter((n) => !n.read_at).length;

  const markAllRead = async () => {
    if (!userId || unread === 0) return;
    const ids = items.filter((n) => !n.read_at).map((n) => n.id);
    await supabase.from("community_notifications").update({ read_at: new Date().toISOString() }).in("id", ids);
    setItems((prev) => prev.map((n) => n.read_at ? n : { ...n, read_at: new Date().toISOString() }));
  };

  const markRead = async (id: string) => {
    await supabase.from("community_notifications").update({ read_at: new Date().toISOString() }).eq("id", id);
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
  };

  return { items, unread, loading, refresh: fetchItems, markAllRead, markRead };
}
